import { ApiResponse, ImportResult } from "../types";
import { CategoryAttributes } from "../types";
import { apiClient } from "./apiClient";
import { authService } from "./authService";
import { API_BASE_URL } from "./config";

const BASE = "/categories";

function buildUrl(
  path: string,
  params?: Record<string, string | number>
): string {
  if (!params || Object.keys(params).length === 0) return path;
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => search.set(k, String(v)));
  return `${path}?${search.toString()}`;
}

export const categoriesApi = {
  async getAll(
    companyId?: number
  ): Promise<ApiResponse<CategoryAttributes[]>> {
    try {
      const url =
        companyId != null ? buildUrl(BASE, { companyId }) : BASE;
      const data = await apiClient.get<any>(url);
      return {
        success: true,
        data: Array.isArray(data)
          ? data
          : (data?.data ?? data?.categories ?? []),
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async getById(id: number): Promise<ApiResponse<CategoryAttributes>> {
    try {
      const data = await apiClient.get<any>(`${BASE}/${id}`);
      return {
        success: true,
        data: data?.data ?? data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async create(
    payload: Omit<CategoryAttributes, "id" | "createdAt" | "updatedAt">
  ): Promise<ApiResponse<CategoryAttributes>> {
    try {
      const data = await apiClient.post<any>(BASE, payload);
      return {
        success: true,
        data: data?.data ?? data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async update(
    id: number,
    payload: Partial<
      Omit<
        CategoryAttributes,
        "id" | "companyId" | "createdAt" | "updatedAt"
      >
    >
  ): Promise<ApiResponse<CategoryAttributes>> {
    try {
      const data = await apiClient.put<any>(`${BASE}/${id}`, payload);
      return {
        success: true,
        data: data?.data ?? data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async delete(id: number): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /**
   * POST /api/categories/import
   * Import categories from Excel (xlsx). Multipart: file, companyId (required).
   * Returns: { message, data: ImportResult } or legacy { categoriesCreated, ... }
   */
  async importFromExcel(
    companyId: number,
    file: File
  ): Promise<ApiResponse<ImportResult>> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("companyId", String(companyId));

      const token = authService.getToken();
      const headers: HeadersInit = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}${BASE}/import`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (response.status === 401) {
        authService.logout();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        if (contentType?.includes("application/json")) {
          try {
            const err = await response.json();
            errorMessage = err.error ?? err.message ?? errorMessage;
          } catch {
            // use default
          }
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get("content-type");
      const json =
        contentType?.includes("application/json")
          ? await response.json()
          : {};
      const data = json?.data ?? json;
      const result: ImportResult = data?.entities
        ? { entities: data.entities, errors: Array.isArray(data?.errors) ? data.errors : [] }
        : {
            entities: {
              categories: {
                created: data?.categoriesCreated ?? 0,
                skipped: data?.categoriesSkipped ?? 0,
              },
              subcategories: {
                created: data?.subcategoriesCreated ?? 0,
                skipped: data?.subcategoriesSkipped ?? 0,
              },
            },
            errors: Array.isArray(data?.errors) ? data.errors : [],
          };
      return {
        success: true,
        data: result,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },
};
